import loggerLocal, { safeToStringLocal } from '@/utils/loggerLocal'

import { i18nGlobal } from '@/i18n'
import { useStoreError } from '@/stores/storeError'
import { useStoreUser } from '@/stores/storeUser'
import { showErrorMessage } from '@/utils/showMessage'
import { DBMutationType, DBRetCode } from '@functions/models/DBEnum'
import type { IntCompanyData } from '@functions/models/interfaces/intCompany'
import type { IntLinkedCompanyData } from '@functions/models/interfaces/intLinkedCompany'
import type { IOListCompanyRequest, IOListCompanyResponse } from '@functions/models/ioInterfaces/ioListCompany'
import type { IOListCompanyLinkRequest, IOListCompanyLinkResponse } from '@functions/models/ioInterfaces/ioListCompanyLink'
import type { IOMutateCompanyRequest, IOMutateCompanyResponse } from '@functions/models/ioInterfaces/ioMutateCompany'
import type { IOMutateCompanyLinkRequest, IOMutateCompanyLinkResponse } from '@functions/models/ioInterfaces/ioMutateCompanyLink'
import {
  be_listCompany,
  be_listCompanyLink,
  be_mutateCompany,
  be_mutateCompanyLink,
} from './backEndCaller'
import { uploadCompanyImage } from './storage'

export interface CallUpdateCompanyRequest {
  company: IntCompanyData
  logoFile?: File
}



export async function callUpdateCompany (
  input: CallUpdateCompanyRequest,
): Promise<IOMutateCompanyResponse | undefined> {
  const storeUser = useStoreUser()
  const storeError = useStoreError()

  try
  {
    const companyId = input.company?.id ?? storeUser.companyData?.id ?? ''
    const inputMutate: IOMutateCompanyRequest = {
      mutationType: DBMutationType.UPDATE,
      company: {
        id: companyId,
        ...input.company,
      },
    }
    if ( !!input.logoFile && input.logoFile !== null )
    {
      //loggerLocal.info('mutateUserData profileImage entered ', profileImage)
      //const resUpload = await uploadCompanyImage(input.logoFile, companyId as string)
      const resUpload = await uploadCompanyImage( input.logoFile )
      if ( resUpload )
      {
        inputMutate.company.logo = resUpload
      } else
      {
        return undefined
      }
    }

    //const resMutate = await be_mutateCompany(inputMutate)
    const resMutate = await callMutateCompany( inputMutate.company, DBMutationType.UPDATE )
    if ( resMutate && resMutate !== undefined )
    {
      const output: IOMutateCompanyResponse = {
        company: resMutate.company,
        result: resMutate.result,
      }
      if ( companyId === storeUser.companyData?.id )
      {
        storeUser.setStoreCompanyData( output.company )
      }
      return output
    } else
    {
      storeError.setError( i18nGlobal.t( 'error.updError' ) + ' callUpdateCompany', resMutate )
      return undefined
    }
  } catch ( error )
  {
    storeError.setError( i18nGlobal.t( 'error.updError' ) + ' callUpdateCompany', error )
    return undefined
  }
}

export async function callMutateCompany (
  company: IntCompanyData,
  mutationType: DBMutationType,
  storeId: boolean = true,
): Promise<IOMutateCompanyResponse | undefined> {
  const functionName = 'callMutateCompany'
  const storeUser = useStoreUser()
  const storeError = useStoreError()
  try
  {
    const companyId = company?.id ?? storeUser.companyData?.id ?? ''
    const inputMutate: IOMutateCompanyRequest = {
      mutationType: mutationType,
      company: {
        id: companyId,
        ...company,
      },
    }
    const resMutate = await be_mutateCompany( inputMutate )
    if ( resMutate && resMutate.result === DBRetCode.SUCCESS )
    {
      const output: IOMutateCompanyResponse = {
        company: resMutate.company,
        result: resMutate.result,
      }
      if ( companyId === storeUser.companyData?.id && storeId )
      {
        loggerLocal.info( functionName, 'Updating storeUser company data', companyId, storeUser.companyData?.id, storeId )
        storeUser.setStoreCompanyData( output.company )
        const linkedCompanyData: IntLinkedCompanyData = storeUser.linkedCompanies.find(
          ( x: IntLinkedCompanyData ) => x.companyId === output.company.id,
        ) as IntLinkedCompanyData
        if ( linkedCompanyData )
        {
          linkedCompanyData.companyName = output.company?.companyName ?? ''
          linkedCompanyData.companyLogo = output.company?.logo ?? ''
          linkedCompanyData.companyStreet = output.company?.streetName ?? ''
          linkedCompanyData.companyCity = output.company?.city ?? ''
          linkedCompanyData.companyProvince = output.company?.province ?? ''
          linkedCompanyData.companyCountry = output.company?.country ?? ''

          storeUser.linkedCompanies = storeUser.linkedCompanies.map( ( x: IntLinkedCompanyData ) =>
            x.companyId === output.company.id ? linkedCompanyData : x,
          )
        }
      }
      return output
    } else
    {
      storeError.setError( i18nGlobal.t( 'error.updError' ) + ' ' + functionName, resMutate )
      return undefined
    }
  } catch ( error )
  {
    // storeError.setError('Errore nella ' + functionName, error)

    storeError.setError( i18nGlobal.t( 'error.updError' ) + ' ' + functionName, error )
    loggerLocal.error( functionName, error )
    return undefined
  }
}

export async function callListCompany ( input: IOListCompanyRequest ): Promise<IOListCompanyResponse> {
  const functionName = callListCompany.name
  let output: IOListCompanyResponse = {
    company: [],
    result: DBRetCode.WARNING,
  }
  try
  {
    const inputCall: IOListCompanyRequest = {
      ...input,
    }
    const res = await be_listCompany( inputCall )
    if ( res.result !== DBRetCode.ERROR )
    {
      output = res
    } else
    {
      showErrorMessage( undefined, res )
    }
  } catch ( error )
  {
    showErrorMessage( undefined, error )
  }
  return output
}

export async function callListCompanyLink ( input: IOListCompanyLinkRequest ): Promise<IOListCompanyLinkResponse> {
  const functionName = callListCompanyLink.name
  let output: IOListCompanyLinkResponse = {
    companyLink: [],
    result: DBRetCode.WARNING,
  }
  try
  {
    const inputCall: IOListCompanyLinkRequest = {
      ...input,
    }
    const res = await be_listCompanyLink( inputCall )
    if ( res.result !== DBRetCode.ERROR )
    {
      output = res
    } else
    {
      showErrorMessage( undefined, res )
    }
  } catch ( error )
  {
    showErrorMessage( undefined, error )
  }
  return output
}

export async function callMutateCompanyLink (
  input: IOMutateCompanyLinkRequest
): Promise<IOMutateCompanyLinkResponse> {
  const defaultOutput: IOMutateCompanyLinkResponse = {
    companyLink: {},
    result: DBRetCode.ERROR,
  };
  try
  {
    const resMutate = await be_mutateCompanyLink( input );
    if ( resMutate && resMutate.result == DBRetCode.SUCCESS )
    {

      return resMutate;
    }
  } catch ( error )
  {
    showErrorMessage( undefined, safeToStringLocal( error ) );

  }
  finally
  {
    return defaultOutput;
  }
}

